import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import { DateSelectorPopover } from '@/components/ui/date-selector/date-selector-popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { checkHotkey, HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import type { Payment } from '@/models/types';
import { useCallback, useState } from 'react';
import { PaymentEntireOrPartialSelect } from './payment-entire-or-partial-select';
import { PaymentMethodSelectorCombobox } from './payment-method-selector-combobox';
import { PaymentTypeSelectorCombobox } from './payment-type-selector-combobox';

//  created_by_id: user.id,
// id: optimisticId,
// amount: 100000,
// // date: dayjs().format("YYYY-MM-DD"),
// // date: "2025-05-17T07:00:00.000Z",
// date: dayjs().format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
// method: "cash", // wire_transfer, personal_check, cash, credit_card, debit_card, cashiers_check, ach_plaid, ach_other
// type: "down_payment", // down_payment, full_amount,
// is_full_payment: false,

const ROW_CLASSNAME = 'flex flex-row gap-2 items-center justify-between';

export type CreatePaymentDialogProps = {
  initialAmount: string;
  initialIntendedPaymentDate: string | undefined;
  initialMethod: Payment['method'];
  initialType: Payment['type'];
  initialIsFullPayment: boolean;
  ctaText: string;
  onOpen: () => void;
  onClose: () => void;
  dialogIsOpen: boolean;
  children?: React.ReactNode;
  onSubmit: (data: {
    amount: string;
    intendedPaymentDate: string | undefined;
    method: Payment['method'];
    type: Payment['type'];
    isFullPayment: Payment['is_full_payment'];
  }) => void;
};

const CreatePaymentDialog = ({
  initialAmount,
  initialIntendedPaymentDate,
  initialMethod,
  initialType,
  initialIsFullPayment,
  onSubmit,
  ctaText,
  onOpen,
  onClose,
  dialogIsOpen,
  children,
}: CreatePaymentDialogProps) => {
  const [amountValue, setAmountValue] = useState(initialAmount);
  const [intendedPaymentDateValue, setIntendedPaymentDateValue] = useState(
    initialIntendedPaymentDate,
  );
  const [methodValue, setMethodValue] =
    useState<Payment['method']>(initialMethod);
  const [typeValue, setTypeValue] = useState<Payment['type']>(initialType);
  const [isFullPaymentValue, setIsFullPaymentValue] =
    useState(initialIsFullPayment);

  const handleSubmit = useCallback(() => {
    onSubmit({
      amount: amountValue,
      intendedPaymentDate: intendedPaymentDateValue,
      method: methodValue,
      type: typeValue,
      isFullPayment: isFullPaymentValue,
    });
  }, [
    amountValue,
    intendedPaymentDateValue,
    methodValue,
    typeValue,
    isFullPaymentValue,
    onSubmit,
  ]);

  return (
    <Dialog open={dialogIsOpen}>
      <DialogTrigger asChild onClick={onOpen}>
        {children}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[425px]"
        onClickClose={onClose}
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onKeyDown={(e) => {
          if (
            checkHotkey({
              hotkey: HOTKEYS.PAYMENT_DIALOG_SUBMIT as string,
              event: e,
            })
          ) {
            handleSubmit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Add payment</DialogTitle>
          <DialogDescription>Add a payment to the deal.</DialogDescription>
        </DialogHeader>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Amount</label>
          <CurrencyInput
            className="rounded text-xs text-right"
            placeholder="Add Amount"
            value={amountValue}
            onChange={(e) => setAmountValue(e.target.value)}
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Intended Payment Date</label>
          <div className="flex flex-row gap-3 items-center">
            <DateSelectorPopover
              date={intendedPaymentDateValue}
              onSelectDate={(date) => {
                setIntendedPaymentDateValue(date?.toISOString() ?? '');
              }}
              side="bottom"
              align="start"
              withHotkeyListener={false}
            />
          </div>
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Method</label>
          <PaymentMethodSelectorCombobox
            value={methodValue}
            setValue={(value) => setMethodValue(value)}
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Type</label>
          <PaymentTypeSelectorCombobox
            value={typeValue}
            setValue={(value) => setTypeValue(value)}
          />
        </div>
        <div className={cn(ROW_CLASSNAME)}>
          <label className="text-sm">Entire Payment</label>
          <PaymentEntireOrPartialSelect
            value={isFullPaymentValue ? 'yes' : 'no'}
            setValue={(value) => setIsFullPaymentValue(value === 'yes')}
          />
        </div>

        <DialogFooter>
          <Button type="submit" onClick={handleSubmit}>
            {ctaText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePaymentDialog;
